import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';

import {
  deleteBill,
  getBillPDF,
  getPendingInvoices,
  markAsInvoiced,
} from '../services/payments';

const useStyles = makeStyles(theme => ({
  table: {
    marginTop: 10,
    padding: 5,
    '& .MuiTableCell-sizeSmall': { padding: theme.spacing(0.75) },
  },
  header: { background: theme.palette.primary.main },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  filtersBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: { minWidth: 160 },
  grow: { flex: 1 },
  noResultsBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
    textAlign: 'center',
  },
}));

const formatCurrency = (value, locale = 'es-AR', currency = 'ARS') => {
  if (value === null || value === undefined || isNaN(Number(value))) return '-';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(value));
  } catch {
    return `${currency} ${Number(value).toFixed(2)}`;
  }
};

export default function BillingTable() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = [10, 25, 50, 100];
  const [refreshToken, setRefreshToken] = useState(0);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceFileError, setInvoiceFileError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const [filters, setFilters] = useState({
    cuit: '',
    razon_social: '',
    fecha_desde: '',
    fecha_hasta: '',
    fecha_pago_desde: '',
    fecha_pago_hasta: '',
    modalidad: '',
    ordering: '-fecha_creacion'
  });

  const [applyToken, setApplyToken] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [referencia, setReferencia] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const totalPages = useMemo(() => {
    if (!total || !pageSize) return 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const pageOptions = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages]
  );

  useEffect(() => {
    fetchData(page, pageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, applyToken, refreshToken]);

  const fetchData = async (pageNumber = 1, perPage = 25, f = {}) => {
    setLoading(true);
    try {
      const params = {
        page: pageNumber,
        page_size: perPage,
        ...f
      };
      if (f.cuit) params.cuit = f.cuit;
      if (f.razon_social) params.razon_social = f.razon_social;

      const resp = await getPendingInvoices(params);
      const data = resp?.data?.results ?? [];
      const count = resp?.data?.count ?? 0;
      setRows(Array.isArray(data) ? data : []);
      setTotal(Number(count) || 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const onChangeFilter = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  const applyFilters = () => {
    setPage(1);
    setApplyToken(t => t + 1);
  };
  const clearFilters = () => {
    setFilters({
      cuit: '',
      razon_social: '',
      fecha_desde: '',
      fecha_hasta: '',
      fecha_pago_desde: '',
      fecha_pago_hasta: '',
      modalidad: '',
      ordering: '-fecha_creacion'
    });
    setPage(1);
    setApplyToken(t => t + 1);
  };

  const handleChangePage = (e) => setPage(Number(e.target.value));
  const handleChangePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };
  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

  const openMarkModal = (item) => {
    setSelected(item);
    setReferencia('');
    setInvoiceFile(null);
    setInvoiceFileError('');
    setOpenModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setOpenModal(false);
    setInvoiceFile(null);
    setInvoiceFileError('');
  };

  const submitMarkAsInvoiced = async () => {
    if (!selected?.id || !referencia?.trim() || !invoiceFile) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('compra', selected.id);
      formData.append('referencia', referencia.trim());
      formData.append('factura_archivo', invoiceFile);

      await markAsInvoiced(formData);

      setOpenModal(false);
      setSelected(null);
      setReferencia('');
      setInvoiceFile(null);
      setInvoiceFileError('');
      setRefreshToken(t => t + 1);
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvoiceFileChange = (e) => {
    const file = e.target.files && e.target.files[0];

    if (!file) {
      setInvoiceFile(null);
      setInvoiceFileError('');
      return;
    }

    if (file.type !== 'application/pdf') {
      setInvoiceFile(null);
      setInvoiceFileError('Solo se permiten archivos PDF.');
      return;
    }

    setInvoiceFile(file);
    setInvoiceFileError('');
  };


  const formatDate = iso => {
    if (!iso) return '-';
    const d = new Date(iso);
    // dd/mm/yyyy hh:mm
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  const headers = [
    'ID',
    'Plan',
    'Extras',
    'Tipo de compra',
    'Razón social',
    'CUIT',
    'Direccion Fiscal',
    'Total (ARS)',
    'Fecha de la compra',
    'Fecha del pago',
    'Modalidad de pago',
    'Condicion fiscal',
    'Acciones',
  ];

  const handleViewInvoice = async (item) => {
    if (!item?.id) return;
    try {
      setDownloadingId(item.id);
      const resp = await getBillPDF(item.id);

      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      window.open(url);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingId(null);
    }
  };

  const openDeleteBillDialog = (item) => {
    setSelected(item);
    setOpenDeleteModal(true);
  };

  const openEditBillDialog = (item) => {
    setSelected(item);
    setOpenEditModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelected(null);
  };

  const closeEditModal = () => {
    setOpenEditModal(false);
    setSelected(null);
  };

  return (
    <>
      <Box p={4} pb={0}>
        <Box className={classes.filtersBar}>
          <TextField
            type="date"
            label="Fecha desde"
            name="fecha_desde"
            value={filters.fecha_desde}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Fecha hasta"
            name="fecha_hasta"
            value={filters.fecha_hasta}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Fecha de pago desde"
            name="fecha_pago_desde"
            value={filters.fecha_pago_desde}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Fecha de pago hasta"
            name="fecha_pago_hasta"
            value={filters.fecha_pago_hasta}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Modalidad de compra"
            name="modalidad"
            value={filters.modalidad}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">Seleccionar modalidad</MenuItem>
            <MenuItem value="mercado_pago">Link de pago</MenuItem>
            <MenuItem value="transferencia">Transferencia</MenuItem>
          </TextField>
          <TextField
            select
            label="Facturadas / No facturadas"
            name="facturada"
            value={filters.facturada}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
          >
            <MenuItem value="all">Todas</MenuItem>
            <MenuItem value="false">No facturadas</MenuItem>
            <MenuItem value="true">Facturadas</MenuItem>
          </TextField>
          <TextField
            select
            label="Ordenar por"
            name="ordering"
            value={filters.ordering}
            onChange={onChangeFilter}
            variant="outlined"
            size="small"
          >
            <MenuItem value="-fecha_creacion">Más recientes</MenuItem>
            <MenuItem value="fecha_creacion">Más antiguos</MenuItem>
            <MenuItem value="fecha_pago">Fecha de pago (ascendente)</MenuItem>
            <MenuItem value="-fecha_pago">Fecha de pago (descendente)</MenuItem>
            <MenuItem value="total">Total (ascendente)</MenuItem>
            <MenuItem value="-total">Total (descendente)</MenuItem>
          </TextField>

        </Box>

        <Box display="flex" gap={8} mb={2}>
          <Button variant="contained" color="primary" onClick={applyFilters}>
            Aplicar filtros
          </Button>
          <Button onClick={clearFilters}>Limpiar</Button>
        </Box>
      </Box>

      {loading ? (
        <Box mt={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box p={4} pt={0}>
          {rows?.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table size="small" aria-label="Compras pendientes de facturación" className={classes.table}>
                  <TableHead>
                    <TableRow className={classes.header}>
                      {headers.map(h => (
                        <TableCell key={h} align="center" className={classes.headerCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell align="center">{item?.id ?? '-'}</TableCell>
                        <TableCell align="center">{item?.nombre_plan ?? '-'}</TableCell>
                        <TableCell align="center">
                          {item.perks_chord &&
                            Object.keys(item.perks_chord).length > 0
                            ? Object.entries(item.perks_chord)
                              .map(([key, value]) => `${key}: ${value}`)
                              .join(', ')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">{item?.tipo_compra ?? '-'}</TableCell>
                        <TableCell align="center">{item?.razon_social || '-'}</TableCell>
                        <TableCell align="center">{item?.cuit || '-'}</TableCell>
                        <TableCell align="center">{item?.direccion_fiscal || '-'}</TableCell>
                        <TableCell align="center">{formatCurrency(item?.total_ars ?? item?.total)}</TableCell>
                        <TableCell align="center">{formatDate(item?.fecha_creacion)}</TableCell>
                        <TableCell align="center">{formatDate(item?.fecha_pago)}</TableCell>
                        <TableCell align="center">{item?.modalidad_compra || '-'}</TableCell>
                        <TableCell align="center">{item?.condicion_fiscal || '-'}</TableCell>
                        {item?.facturada ? (
                          <TableCell align="left" style={{ display: 'flex' }}>
                            <Tooltip title="Ver factura">
                              <IconButton onClick={() => handleViewInvoice(item)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>


                            <Tooltip title="Editar factura">
                              <IconButton onClick={() => openEditBillDialog(item)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>


                            <Tooltip title="Eliminar factura">
                              <IconButton onClick={() => openDeleteBillDialog(item)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell align="left" style={{ display: 'flex' }}>
                            <Tooltip title="Marcar como facturada">
                              <span>
                                <IconButton
                                  aria-label="Marcar como facturada"
                                  onClick={() => openMarkModal(item)}
                                  disabled={submitting}
                                  size="small"
                                >
                                  <AssignmentTurnedInIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box className={classes.toolbar}>
                <FormControl variant="outlined" size="small" className={classes.formControl}>
                  <InputLabel id="page-size-label">Cantidad</InputLabel>
                  <Select
                    labelId="page-size-label"
                    value={pageSize}
                    onChange={handleChangePageSize}
                    label="Cantidad por página"
                  >
                    {pageSizeOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" className={classes.formControl} disabled={totalPages === 0}>
                  <InputLabel id="page-label">Página</InputLabel>
                  <Select
                    labelId="page-label"
                    value={Math.min(page, totalPages || 1)}
                    onChange={handleChangePage}
                    label="Página"
                  >
                    {pageOptions.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton onClick={handlePrev} disabled={page <= 1} aria-label="Anterior">
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton onClick={handleNext} disabled={page >= totalPages} aria-label="Siguiente">
                  <ChevronRightIcon />
                </IconButton>

                <Box className={classes.grow} />
                <Box>Página {page} de {totalPages || 1} — Total: {total}</Box>
              </Box>
            </>
          ) : (
            <Box className={classes.noResultsBox}>
              Ya se facturaron todas las compras.
            </Box>
          )}
        </Box>
      )}

      <Dialog open={openModal} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar como facturada</DialogTitle>
        <DialogContent dividers>
          <Box display="grid" gridGap={12}>
            <div>Vas a marcar la compra <b>#{selected?.id}</b> como facturada.</div>
            <TextField
              label="Referencia de la factura"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              variant="outlined"
              size="small"
              autoFocus
              fullWidth
              required
              helperText="Ingresá el código de referencia que emitió AFIP / sistema."
            />
            <div>
              <Button
                variant="outlined"
                component="label"
                color={'primary'}
              >
                {invoiceFile ? 'Cambiar PDF de factura' : 'Subir PDF de factura'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={handleInvoiceFileChange}
                />
              </Button>
              {invoiceFile && (
                <Box mt={1} style={{ fontSize: 12 }}>
                  Archivo seleccionado: <b>{invoiceFile.name}</b>
                </Box>
              )}
              {invoiceFileError && (
                <Box mt={1} style={{ fontSize: 12, color: 'red' }}>
                  {invoiceFileError}
                </Box>
              )}
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={submitting}>Cancelar</Button>
          <Button
            onClick={submitMarkAsInvoiced}
            color="primary"
            variant="contained"
            disabled={submitting || !referencia.trim() || !invoiceFile}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteModal} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar factura</DialogTitle>


        <DialogContent dividers>
          <Box>
            Vas a eliminar la factura de la compra <b>#{selected?.id}</b>.
            <br />
            Esta acción no se puede deshacer.
          </Box>
        </DialogContent>


        <DialogActions>
          <Button onClick={closeDeleteModal} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={submitting}
            onClick={async () => {
              try {
                setSubmitting(true);
                await deleteBill(selected.id);
                closeDeleteModal();
                fetchData(page, pageSize, filters);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditModal} onClose={closeEditModal} maxWidth="xs" fullWidth>
        <DialogTitle>Editar factura</DialogTitle>

        <DialogContent dividers>
          <Box display="grid" gridGap={12}>
            <div>
              Vas a editar la factura de la compra <b>#{selected?.id}</b>.
              <br />
              La factura actual será eliminada y reemplazada.
            </div>

            <TextField
              label="Referencia de la factura"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              variant="outlined"
              size="small"
              autoFocus
              fullWidth
              required
              helperText="Ingresá el código de referencia que emitió AFIP / sistema."
            />

            <div>
              <Button
                variant="outlined"
                component="label"
                color="primary"
              >
                {invoiceFile ? 'Cambiar PDF de factura' : 'Subir PDF de factura'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={handleInvoiceFileChange}
                />
              </Button>

              {invoiceFile && (
                <Box mt={1} style={{ fontSize: 12 }}>
                  Archivo seleccionado: <b>{invoiceFile.name}</b>
                </Box>
              )}

              {invoiceFileError && (
                <Box mt={1} style={{ fontSize: 12, color: 'red' }}>
                  {invoiceFileError}
                </Box>
              )}
            </div>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeEditModal} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              try {
                setSubmitting(true);
                await deleteBill(selected.id);
                await submitMarkAsInvoiced();
                closeEditModal();
                fetchData(page, pageSize, filters);
              } finally {
                setSubmitting(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={submitting || !referencia.trim() || !invoiceFile}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
